import { BaseURL } from './api';

// Signs the user out the moment the API stops accepting their session.
//
// Why this exists: being "logged in" was decided purely by the `user-info` key in localStorage
// (see layout/labadmin/index.jsx), and nothing in the app ever removed that key except the
// Logout button. So when the lab owner changed an employee's password or removed their login,
// the API began refusing every request while the browser carried on rendering the panel - the
// employee saw empty tables instead of a login screen, and looked to the owner like they were
// still signed in.
//
// Why window.fetch rather than each caller: there are 82 fetch calls across 42 files. Wrapping
// once here covers all of them, including ones added later, and leaves every call site alone.
// Nothing in the app holds its own reference to fetch, so patching the global reaches them all.

const ORIGINAL_FETCH = window.fetch.bind(window);

// One sign-out only. Several requests usually fail together, and each would otherwise start its
// own navigation.
let signingOut = false;

const isOurApi = (input) => {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    return typeof url === 'string' && url.indexOf(BaseURL) === 0;
};

const hasSession = () => {
    // Missing key makes JSON.parse throw, hence the guard rather than a plain read.
    try { return !!JSON.parse(localStorage.getItem('user-info')); }
    catch (e) { return false; }
};

const signOut = () => {
    if (signingOut) { return; }
    signingOut = true;
    localStorage.removeItem('user-info');
    // A real navigation, exactly like the Logout button (pages/signout.jsx). About 40 modules
    // read user-info once at module scope, so a React route change would leave them holding the
    // old user; only a document load clears that.
    document.location.replace('/login?expired=1');
};

export const installAuthGuard = () => {
    window.fetch = (...args) => ORIGINAL_FETCH(...args).then((res) => {
        // 401 is the API saying "this token is no longer accepted" - the account was deleted,
        // its password changed, it was deactivated, or it signed in somewhere else.
        //
        // 403 deliberately does NOT sign anyone out. It means the session is fine but that one
        // endpoint is outside the employee's permissions, which is an ordinary answer for a
        // limited account - signing them out for it would make half the app log them off.
        if (res.status === 401 && isOurApi(args[0]) && hasSession()) {
            signOut();
        }
        return res;
    });
};

export default installAuthGuard;
